import React, { useCallback, useContext, useEffect } from 'react';
import { Transition, Container } from '@printdeal/ui-library';
import clsx from 'clsx';
import { HeaderContext } from '../../context';

type DesktopNavigationDropdownProps = {
  children: React.ReactNode
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  unmount?: boolean;
  className?: string;
};

const DesktopNavigationDropdown = ({
  children,
  isOpen,
  setIsOpen,
  unmount = false,
  className,
  ...rest
}: DesktopNavigationDropdownProps) => {
  const { isSticky } = useContext(HeaderContext);

  // Close on escape key press.
  const handleWindowKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.defaultPrevented) {
      // Do nothing if the event was already processed.
      return;
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
      // Cancel the default action to avoid it being handled twice.
      event.preventDefault();
    }
  }, [setIsOpen]);

  // Mount effect.
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleWindowKeyPress);
    } else {
      window.removeEventListener('keydown', handleWindowKeyPress);
    }
    return () => {
      // Cleanup escape key listener just in case.
      window.removeEventListener('keydown', handleWindowKeyPress);
    };
  }, [isOpen, handleWindowKeyPress]);

  return (
    <Transition
      show={isOpen}
      unmount={unmount}
      duration="sm"
      {...rest}
      className={clsx('pd-absolute pd-inset-0 pd-top-full -pd-z-20 -pd-mt-px pd-transform-gpu', className)}
    >
      <Transition.Child unmount={unmount} variant="appear">
        <div className="pd-pointer-events-none pd-absolute pd-inset-x-0 pd-top-0 pd-h-[200vh] pd-bg-black pd-opacity-20" />
      </Transition.Child>
      <Transition.Child
        unmount={unmount}
        variant="appear-from-top"
        className={clsx(
          'pd-relative pd-overflow-auto pd-border-t pd-bg-white pd-shadow-lg',
          isSticky ? 'pd-max-h-[calc(95vh-5.5625rem)]' : 'pd-max-h-[calc(95vh-9rem)]',
        )}
      >
        {/* Gradient scroll indicator top */}
        <div className="pd-sticky pd-inset-x-0 pd-top-0 pd-z-10 pd-h-7 pd-bg-gradient-to-b pd-from-white" />
        <Container className="pd-relative">
          {children}
        </Container>
        {/* Gradient scroll indicator bottom */}
        <div className="pd-sticky pd-inset-x-0 pd-bottom-0 pd-z-10 pd-h-7 pd-bg-gradient-to-t pd-from-white" />
      </Transition.Child>
    </Transition>
  );
};

export default DesktopNavigationDropdown;
